import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "lenis";
import Grain from "@/components/Grain";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import HomePage from "@/pages/HomePage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import TheFivePage from "@/pages/TheFivePage";
import ConciergePage from "@/pages/ConciergePage";
import MyDatePage from "@/pages/MyDatePage";
import MembershipPage from "@/pages/MembershipPage";
import FaqPage from "@/pages/FaqPage";
import SupportPage from "@/pages/SupportPage";
import ContactPage from "@/pages/ContactPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import AccountDeletionPage from "@/pages/AccountDeletionPage";
import WelcomePage from "@/pages/WelcomePage";
import CurtainTransition from "@/components/CurtainTransition";
import GizlilikPage from "@/pages/GizlilikPage";
import KvkkPage from "@/pages/KvkkPage";
import KullanimKosullariPage from "@/pages/KullanimKosullariPage";

const EASE = [0.16, 1, 0.3, 1];

function SmoothScroll() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const VARIANTS = {
  initial: (curtain) => (curtain ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }),
  animate: (curtain) => ({ opacity: 1, y: 0, transition: { duration: curtain ? 0 : 0.45, ease: EASE } }),
  exit: (curtain) => ({ opacity: curtain ? 1 : 0, transition: { duration: curtain ? 0 : 0.3 } }),
};

function AnimatedRoutes() {
  const location = useLocation();
  const curtain = Boolean(location.state?.curtain);
  return (
    <AnimatePresence mode="wait" custom={curtain}>
      <motion.main
        key={location.pathname}
        custom={curtain}
        variants={VARIANTS}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <Routes location={location}>
          <Route path="/" element={<WelcomePage />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/nasil-calisir" element={<HowItWorksPage />} />
          <Route path="/the-five" element={<TheFivePage />} />
          <Route path="/konsiyerj" element={<ConciergePage />} />
          <Route path="/my-date" element={<MyDatePage />} />
          <Route path="/uyelik" element={<MembershipPage />} />
          <Route path="/sss" element={<FaqPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/account-deletion" element={<AccountDeletionPage />} />
          <Route path="/gizlilik" element={<GizlilikPage />} />
          <Route path="/kvkk" element={<KvkkPage />} />
          <Route path="/kullanim-kosullari" element={<KullanimKosullariPage />} />
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
      </motion.main>
    </AnimatePresence>
  );
}

function Shell() {
  const { pathname, state } = useLocation();
  const immersive = pathname === "/";
  useEffect(() => {
    if (immersive) document.documentElement.classList.add("stage");
    else if (!state?.curtain) document.documentElement.classList.remove("stage");
  }, [immersive, state]);
  return (
    <>
      <SmoothScroll />
      <ScrollToTop />
      <Grain />
      {!immersive && <Nav />}
      <AnimatedRoutes />
      {!immersive && <Footer />}
      <CurtainTransition />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}
